import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { SideKickService } from '../services/sidekick.service';
import { SideKickSignalrService } from '../services/sidekick.signalr.service';
import { ESideKickRequestStatus } from '../services/sidekick.do';

export interface ICodeReviewModalInParams {
  applicationId: number;
}

@Component({
  selector: 'app-code-review-modal',
  templateUrl: './code-review-modal.component.html',
  styleUrls: ['./code-review-modal.component.scss'],
  standalone: false
})
export class CodeReviewModalComponent implements OnInit, OnDestroy {
  @ViewChild('contentArea') contentArea: ElementRef<HTMLElement>;

  // State properties
  codeReviewMarkdown: string | null = null;
  isWaitingForCodeReview = false;
  currentSuggestionId: number | null = null;
  canScrollToTop = false;

  private sideKickSubscription: Subscription;

  constructor(
    private dialogRef: MatDialogRef<CodeReviewModalComponent>,
    @Inject(MAT_DIALOG_DATA) public params: ICodeReviewModalInParams,
    private sideKickService: SideKickService,
    private sideKickSignalrService: SideKickSignalrService
  ) { }

  ngOnInit(): void {
    // 1. Connect to SignalR for real-time updates
    this.sideKickSignalrService.connectCodeReview(this.params.applicationId);

    // 2. Subscribe to completion events
    this.sideKickSubscription = this.sideKickSignalrService.codeReviewCompleted$
      .subscribe(result => {
        this.codeReviewMarkdown = result.codeReviewMarkdown;
        this.currentSuggestionId = result.suggestionId;
        this.isWaitingForCodeReview = false;
      });

    // 3. Check for existing or pending code review
    this.sideKickService.getCodeReview(this.params.applicationId).then(result => {
      if (result?.requestStatusId === ESideKickRequestStatus.pending) {
        this.isWaitingForCodeReview = true;
        this.currentSuggestionId = result.suggestionId;
      } else if (result?.codeReviewMarkdown) {
        this.codeReviewMarkdown = result.codeReviewMarkdown;
        this.currentSuggestionId = result.suggestionId;
      }
    });
  }

  ngOnDestroy(): void {
    this.sideKickSubscription?.unsubscribe();
    this.sideKickSignalrService.disconnect();
  }

  async onGenerateClicked(): Promise<void> {
    this.isWaitingForCodeReview = true;
    try {
      this.currentSuggestionId = await this.sideKickService.codeReview(this.params.applicationId);
    } catch (e) {
      this.isWaitingForCodeReview = false;
      throw e;
    }
  }

  async onStopClicked(): Promise<void> {
    if (this.currentSuggestionId) {
      await this.sideKickService.stopSideKickSuggestion(this.currentSuggestionId);
      this.isWaitingForCodeReview = false;
    }
  }

  onContentScroll(event: Event): void {
    const target = event.target as HTMLElement;
    // Enable "Back to top" when scrolled more than one container height
    this.canScrollToTop = target.scrollTop > target.clientHeight;
  }

  scrollToTop(): void {
    if (this.contentArea?.nativeElement) {
      this.contentArea.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
